import { useState } from "react";
import { MapPin, DollarSign, Home, ArrowRight, ArrowLeft, Check, Search, TrendingUp, Bell } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";

const STEPS = [
  { id: "welcome", title: "Welcome to RealixData", icon: TrendingUp },
  { id: "location", title: "Where are you looking?", icon: MapPin },
  { id: "budget", title: "What's your budget?", icon: DollarSign },
  { id: "strategy", title: "Investment strategy", icon: Home },
];

const OnboardingModal = ({ isOpen, onClose, onComplete, userName }) => {
  const [step, setStep] = useState(0);
  const [preferences, setPreferences] = useState({
    locations: [],
    budgetMin: "",
    budgetMax: "",
    strategies: [],
  });
  const [locationInput, setLocationInput] = useState("");

  const addLocation = () => {
    const loc = locationInput.trim();
    if (loc && !preferences.locations.includes(loc)) {
      setPreferences(p => ({ ...p, locations: [...p.locations, loc] }));
      setLocationInput("");
    }
  };

  const removeLocation = (loc) => {
    setPreferences(p => ({ ...p, locations: p.locations.filter(l => l !== loc) }));
  };

  const toggleStrategy = (s) => {
    setPreferences(p => ({
      ...p,
      strategies: p.strategies.includes(s)
        ? p.strategies.filter(x => x !== s)
        : [...p.strategies, s],
    }));
  };

  const handleComplete = () => {
    onComplete(preferences);
    onClose();
  };

  const canProceed = step === 0 || step === 3 ||
    (step === 1 && preferences.locations.length > 0) ||
    (step === 2);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px] w-[95vw] p-0 bg-card border-border overflow-hidden" data-testid="onboarding-modal">
        <DialogTitle className="sr-only">Welcome to RealixData</DialogTitle>
        {/* Progress bar */}
        <div className="h-1 bg-secondary">
          <div
            className="h-full bg-emerald-500 transition-all duration-500"
            style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
          />
        </div>

        <div className="px-6 pt-6 pb-2">
          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-6">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  i < step ? 'bg-emerald-500 text-white' :
                  i === step ? 'bg-emerald-500/15 text-emerald-500 border border-emerald-500/30' :
                  'bg-secondary text-muted-foreground'
                }`}>
                  {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </div>
                {i < STEPS.length - 1 && <div className={`w-6 h-px ${i < step ? 'bg-emerald-500' : 'bg-border'}`} />}
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 pb-6">
          {/* Step 0: Welcome */}
          {step === 0 && (
            <div className="text-center py-4" data-testid="onboarding-step-welcome">
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-5">
                <TrendingUp className="w-8 h-8 text-emerald-500" />
              </div>
              <h2 className="text-xl font-bold mb-2">
                {userName ? `Welcome, ${userName.split(' ')[0]}!` : 'Welcome!'}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-sm mx-auto">
                Let's personalize your experience in 30 seconds. We'll use your preferences to surface the best deals for you.
              </p>
            </div>
          )}

          {/* Step 1: Location */}
          {step === 1 && (
            <div data-testid="onboarding-step-location">
              <h2 className="text-lg font-bold mb-1">Where are you looking to invest?</h2>
              <p className="text-sm text-muted-foreground mb-4">Add one or more cities or states</p>
              <div className="flex gap-2 mb-3">
                <Input
                  value={locationInput}
                  onChange={(e) => setLocationInput(e.target.value)}
                  placeholder="e.g. Tampa FL, Houston TX..."
                  className="bg-secondary/30 border-border"
                  data-testid="onboarding-location-input"
                  onKeyDown={(e) => e.key === 'Enter' && addLocation()}
                />
                <Button variant="outline" size="sm" onClick={addLocation} className="px-4">Add</Button>
              </div>
              {preferences.locations.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {preferences.locations.map((loc) => (
                    <Badge key={loc} className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 cursor-pointer hover:bg-red-500/10 hover:text-red-500 hover:border-red-500/20 transition-colors" onClick={() => removeLocation(loc)}>
                      {loc} <span className="ml-1">x</span>
                    </Badge>
                  ))}
                </div>
              )}
              <div className="mt-4">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Popular markets</p>
                <div className="flex flex-wrap gap-1.5">
                  {["Tampa FL", "Miami FL", "Houston TX", "Atlanta GA", "Dallas TX", "Phoenix AZ"].map(city => (
                    <button
                      key={city}
                      onClick={() => !preferences.locations.includes(city) && setPreferences(p => ({ ...p, locations: [...p.locations, city] }))}
                      className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                        preferences.locations.includes(city)
                          ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500'
                          : 'bg-secondary/30 border-border text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={`onboarding-popular-${city.replace(/\s/g, '-')}`}
                    >
                      {city}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Budget */}
          {step === 2 && (
            <div data-testid="onboarding-step-budget">
              <h2 className="text-lg font-bold mb-1">What's your budget range?</h2>
              <p className="text-sm text-muted-foreground mb-4">Optional — helps us prioritize relevant deals</p>
              <div className="flex gap-3 mb-4">
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground mb-1 block">Min Price</label>
                  <Input
                    type="number"
                    value={preferences.budgetMin}
                    onChange={(e) => setPreferences(p => ({ ...p, budgetMin: e.target.value }))}
                    placeholder="$100,000"
                    className="bg-secondary/30 border-border font-mono"
                    data-testid="onboarding-budget-min"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground mb-1 block">Max Price</label>
                  <Input
                    type="number"
                    value={preferences.budgetMax}
                    onChange={(e) => setPreferences(p => ({ ...p, budgetMax: e.target.value }))}
                    placeholder="$500,000"
                    className="bg-secondary/30 border-border font-mono"
                    data-testid="onboarding-budget-max"
                  />
                </div>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Quick select</p>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { label: "Under $200K", min: "", max: "200000" },
                    { label: "$200K-$400K", min: "200000", max: "400000" },
                    { label: "$400K-$700K", min: "400000", max: "700000" },
                    { label: "$700K+", min: "700000", max: "" },
                  ].map(({ label, min, max }) => (
                    <button
                      key={label}
                      onClick={() => setPreferences(p => ({ ...p, budgetMin: min, budgetMax: max }))}
                      className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                        preferences.budgetMin === min && preferences.budgetMax === max
                          ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500'
                          : 'bg-secondary/30 border-border text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Strategy */}
          {step === 3 && (
            <div data-testid="onboarding-step-strategy">
              <h2 className="text-lg font-bold mb-1">What's your investment focus?</h2>
              <p className="text-sm text-muted-foreground mb-4">Select all that apply</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: "cash_flow", label: "Cash Flow", desc: "Monthly income", icon: DollarSign },
                  { id: "appreciation", label: "Appreciation", desc: "Long-term growth", icon: TrendingUp },
                  { id: "brrrr", label: "BRRRR", desc: "Buy-rehab-rent", icon: Home },
                  { id: "distressed", label: "Distressed/Seized", desc: "Below market", icon: Search },
                  { id: "multi_family", label: "Multi-Family", desc: "2-4+ units", icon: Home },
                  { id: "alerts", label: "Deal Alerts", desc: "Email notifications", icon: Bell },
                ].map(({ id, label, desc, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => toggleStrategy(id)}
                    className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-all ${
                      preferences.strategies.includes(id)
                        ? 'bg-emerald-500/10 border-emerald-500/30 ring-1 ring-emerald-500/20'
                        : 'bg-secondary/20 border-border hover:border-border/80'
                    }`}
                    data-testid={`onboarding-strategy-${id}`}
                  >
                    <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${preferences.strategies.includes(id) ? 'text-emerald-500' : 'text-muted-foreground'}`} />
                    <div>
                      <p className={`text-sm font-medium ${preferences.strategies.includes(id) ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</p>
                      <p className="text-[10px] text-muted-foreground">{desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
            {step > 0 ? (
              <Button variant="ghost" size="sm" onClick={() => setStep(s => s - 1)} data-testid="onboarding-back">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={onClose} className="text-muted-foreground" data-testid="onboarding-skip">
                Skip for now
              </Button>
            )}
            {step < STEPS.length - 1 ? (
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => setStep(s => s + 1)}
                disabled={!canProceed}
                data-testid="onboarding-next"
              >
                Continue <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={handleComplete}
                data-testid="onboarding-finish"
              >
                <Check className="w-4 h-4 mr-1" /> Start Searching
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;
